import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { COPY_ASSESSMENT } from 'store/assessments/actions';
import { selectAseAssetId } from 'store/assessments/selectors';
import {
  selectAsset,
  selectAssetListForOrganisation,
} from 'store/assets/selectors';
import { selectOrganisation } from 'store/organisations/selectors';

export const CopyAssessmentScreen: React.FC<
  ModalScreenProps<ModalScreens.ASECOPY>
> = () => {
  const dispatch = useDispatch();
  const [selectedAssetIds, setSelectedAssetIds] = React.useState<string[]>([]);
  const assetId = useSelector(selectAseAssetId);
  const asset = useSelector((state) => selectAsset(state, assetId));
  const assets = useSelector((state) =>
    selectAssetListForOrganisation(state, asset.organisation_id),
  ).filter((otherAsset) => otherAsset.id !== asset.id);

  const organisation = useSelector((state) =>
    selectOrganisation(state, asset.organisation_id),
  );

  const handlePressAsset = React.useCallback(
    (newAssetId: string) => () =>
      setSelectedAssetIds((currentSelectedAssetIds) =>
        currentSelectedAssetIds.includes(newAssetId)
          ? without(currentSelectedAssetIds, newAssetId)
          : [...currentSelectedAssetIds, newAssetId],
      ),
    [],
  );

  const handlePressCopy = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(COPY_ASSESSMENT({ assetIds: selectedAssetIds }));
  };

  useEsc(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  });

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Copy Assessment"
          onPressSaveButton={selectedAssetIds.length ? handlePressCopy : null}
          saveButtonText="Copy"
          onPressCancelButton={() => dispatch(NAVIGATE_MODAL_BACK())}
        />
      }
    >
      <CardGeneric>
        <Txt>
          Choose assets to copy this assessment to. The assessment&apos;s custom
          reference, file attachments and photos will not be copied.
        </Txt>
      </CardGeneric>

      <Card label={`Assets from ${organisation.details.name}`}>
        {!assets.length ? (
          <CardRow
            icon={ICONS.CircleExclamationLight}
            value={
              'There are no assets to copy this assessment to. ' +
              `Make sure you have another asset from ${organisation.details.name} downloaded.`
            }
          />
        ) : (
          assets.map((otherAsset) => (
            <CardRow
              key={otherAsset.id}
              checkedValue={selectedAssetIds.includes(otherAsset.id)}
              onPress={handlePressAsset(otherAsset.id)}
            >
              <ItemDetails
                title={otherAsset.details.name}
                subtitle={
                  [
                    otherAsset.details.parentName,
                    otherAsset.details.address1,
                    otherAsset.details.address2,
                    otherAsset.details.city,
                    otherAsset.details.county,
                    otherAsset.details.postcode,
                  ]
                    .filter((item) => !!item)
                    .join(', ') || undefined
                }
                subsubtitle={
                  rbAddReferencePrefix(otherAsset.reference) +
                  (otherAsset.details.custom_reference
                    ? ` • ${otherAsset.details.custom_reference}`
                    : '')
                }
              />
            </CardRow>
          ))
        )}
      </Card>
    </PageScrollView>
  );
};
